import InformationCircle from '@ingka/ssr-icon/paths/information-circle';
import { ComponentIds, Entity, SelectableComponent } from '@inter-ikea-core/core';
import { InteriorComponent, Product } from '@inter-ikea-storageone/range';
import MobileDrawer from 'components/MobileDrawer/MobileDrawer';
import MobileSwiper from 'components/MobileSwiper/MobileSwiper';
import NoneOptionCard from 'components/ProductSidebar/ProductCard/NoneOptionCard';
import ProductCard from 'components/ProductSidebar/ProductCard/ProductCard';
import { List, ListItem } from 'components/ProductSidebar/menus/menuComponents';
import SelectInSceneInfo from 'components/SelectInSceneInfo/SelectInSceneInfo';
import { useCore } from 'contexts/core';
import { hideSlidingDoorPullOutMessage } from 'rangeConfig/platsa/rules/hideMenuRules';
import { useCallback, useEffect, useState } from 'react';
import { useIntl } from 'react-intl';
import styled from 'styled-components';
import Box from 'ui/Box';
import SkapaInlineMessage, { InlineMessageVariantEnum } from 'ui/InlineMessage/SkapaInlineMessage';
import media from 'ui/media';
import { getRange } from 'utils/queryUtils';
import { useLocation } from 'react-router-dom';
import { AppRoutes } from 'routes';

type Props = {
  selectedEntity: Entity | null;
  selectedGroupId: string;
  suggestionsForSelectedFrame: Product[];
  handleClick: (product: Product) => void;
  message: string;
  image: string;
  noneOptionForInteriorSuggestions: any;
};

const Wrapper = styled(Box)`
  padding: ${({ theme }) => theme.space.s} ${({ theme }) => theme.space.xxl}
    ${({ theme }) => theme.space.s} ${({ theme }) => theme.space.xxl};
  ${media.mobile`
  padding: 0.5rem 0.75rem 0.75rem 0.75rem;
`};
`;

export default function InteriorSuggestionsMobile({
  selectedEntity,
  selectedGroupId,
  suggestionsForSelectedFrame,
  handleClick,
  message,
  image,
  noneOptionForInteriorSuggestions
}: Props) {
  const coreApi = useCore();
  const [showMobileDrawer, setShowMobileDrawer] = useState(false);
  const { formatMessage } = useIntl();
  // If current page is summary, we should hide the mobile drawer as that causes the summary page to not be usable on mobile devices
  const currentPageIsSummary = useLocation().pathname === AppRoutes.SUMMARY;

  const setInteriorsInSceneSelectable = useCallback(
    (selectable: boolean) => {
      const interiorEntitiesInScene = coreApi.entities.getByComponent(InteriorComponent.id);
      if (selectable) {
        interiorEntitiesInScene.forEach(entity => {
          entity.addComponent(new SelectableComponent());
        });
      } else {
        interiorEntitiesInScene.forEach(entity => {
          entity.removeComponent(ComponentIds.SELECTABLE_COMPONENT_ID);
        });
      }
    },
    [coreApi]
  );

  useEffect(() => {
    setInteriorsInSceneSelectable(false);
    return () => {
      setInteriorsInSceneSelectable(true);
    };
  }, [setInteriorsInSceneSelectable]);

  const onAdditionalItemsClicked = () => {
    setShowMobileDrawer(true);
  };

  if (!selectedEntity) {
    return <SelectInSceneInfo message={message} image={image} />;
  }

  return (
    <>
      <MobileSwiper
        id="interior-suggestions-product-list"
        onSelect={handleClick}
        numberOfItems={10}
        onAdditionalItemsClicked={onAdditionalItemsClicked}
        productClusters={suggestionsForSelectedFrame.map(p => [p])}
      />
      <MobileDrawer
        open={showMobileDrawer && !currentPageIsSummary}
        title={formatMessage({ id: 'storageone.interiorSuggestionsCombinationsTabTitle' })}
        onDismiss={() => setShowMobileDrawer(false)}
      >
        {getRange() === 'platsa' && !hideSlidingDoorPullOutMessage(coreApi) && (
          <Wrapper>
            <SkapaInlineMessage
              body={formatMessage({ id: 'storageone.combinationInteriorInlineMessage' })}
              variant={InlineMessageVariantEnum.INFORMATIVE}
              ssrIcon={InformationCircle}
              subtle
            />
          </Wrapper>
        )}

        <List>
          <ListItem key="none-option">
            <NoneOptionCard
              noneOption={noneOptionForInteriorSuggestions}
              currentMenu={selectedGroupId}
            ></NoneOptionCard>
          </ListItem>
          {suggestionsForSelectedFrame.map((product: Product) => (
            <ListItem key={product.coreProductId} data-testid={product.coreProductId}>
              <ProductCard
                product={product}
                handleClick={handleClick}
                infoButtonAriaLabel={'storageone.selectedMenuInteriorsCombinationsMenuTitle'}
              />
            </ListItem>
          ))}
        </List>
      </MobileDrawer>
    </>
  );
}
